import { DetailedHTMLProps, forwardRef, InputHTMLAttributes, ReactNode, useEffect, useRef, useState } from 'react'

import cn from 'clsx'

import s from './TradeInput.module.scss'

interface ITradeInputProps extends DetailedHTMLProps<InputHTMLAttributes<HTMLInputElement>, HTMLInputElement> {
  leftPlaceholder?: string
  ariaDescribedby?: string
  sourceCurrency?: string
  targetCurrency?: string
  error?: string
  rate?: number
  tipText?: string
  label?: string
  showControls?: boolean
  subValue?: string
  onUpdateValue?: (val: string) => void
  containerClassNames?: string
  renderElements?: (isFocus?: boolean, isError?: boolean) => ReactNode
}

interface IModalProps {
  text: string
}

export const TradeInput = forwardRef<HTMLInputElement, ITradeInputProps>(
  (
    {
      id,
      value,
      ariaDescribedby,
      targetCurrency = '',
      error,
      tipText,
      disabled = false,
      label,
      showControls,
      onFocus,
      onBlur,
      onUpdateValue,
      onChange,
      subValue,
      className,
      containerClassNames,
      renderElements,
      ...props
    },
    ref,
  ) => {
    const [isFocus, setIsFocus] = useState(false)
    const localRef = useRef<HTMLInputElement>(null)

    const combinedRef = (el: HTMLInputElement) => {
      localRef.current = el
      if (typeof ref === 'function') ref(el)
      else if (ref) (ref as React.RefObject<HTMLInputElement | null>).current = el
    }

    const changeValue = (increase: boolean) => {
      const nativeInputValueSetter = Object?.getOwnPropertyDescriptor?.(window.HTMLInputElement.prototype, 'value')?.set

      const current = localRef.current

      if (nativeInputValueSetter && current && typeof Number(localRef.current?.value) === 'number') {
        const numberValue = Number(localRef.current?.value)
        const stepValue = props?.step ? Number(props.step) : 1
        const diff = increase ? stepValue : -stepValue

        const precision = (stepValue.toString().split('.')[1] || '').length

        const nextValue = Number((numberValue + diff).toFixed(precision))
        nativeInputValueSetter.call(current, nextValue)
        const event = new Event('input', { bubbles: true })
        current.dispatchEvent(event)
      }
    }
    useEffect(() => {
      if (localRef.current?.value) {
        onUpdateValue?.(localRef.current?.value)
      }
      return () => {
        onUpdateValue?.('')
      }
    }, [localRef.current])
    const targetCurrencyLength = targetCurrency.length > 1 ? targetCurrency.length : 1.6
    return (
      <div
        style={
          {
            '--leftOffset': targetCurrencyLength || 1,
          } as React.CSSProperties
        }
        className={cn(
          s.inputContainer,
          isFocus && s.active,
          error && s.error,
          disabled && s.disabled,
          containerClassNames,
        )}
      >
        {label && (
          <label className={s.label} htmlFor={id}>
            {label}
          </label>
        )}

        <div className={s.inputWrapper}>
          <input
            ref={combinedRef}
            value={value}
            autoComplete="off"
            aria-describedby={ariaDescribedby}
            className={cn(s.input, className)}
            disabled={disabled}
            onFocus={(e) => {
              setIsFocus(true)
              onFocus?.(e)
            }}
            onBlur={(e) => {
              setIsFocus(false)
              onBlur?.(e)
            }}
            onChange={(e) => {
              onChange?.(e)
              onUpdateValue?.(e.target.value)
            }}
            {...props}
          />
          {showControls && (
            <span className={cn(s.controls)}>
              <div className={s.minusContainer} onClick={() => changeValue(false)}>
                <span className={s.minus} />
              </div>
              <span className={s.plus} onClick={() => changeValue(true)}>
                +
              </span>
            </span>
          )}
          {renderElements?.(isFocus, !!error)}
          {subValue && <span className={cn(s.subValue)}>{subValue}</span>}
          <span className={cn(s.inputPlaceholder)}>{targetCurrency}</span>
        </div>

        {error && <span className={s.errorMessage}>{error}</span>}
      </div>
    )
  },
)

TradeInput.displayName = 'TradeInput'
